/**
 * `CanvasDownloadMenu` — le bouton « Télécharger » du header canvas, devenu un
 * MENU à deux sorties (Word / PDF) le 2026-08-26.
 *
 * Extrait de `RedactionPanel` à cette occasion : le composant a gagné un menu et
 * un appel réseau, et surtout il n'était testable nulle part tant qu'il vivait
 * dans un panneau qui exige tout le contexte du canvas pour se monter. Le repo
 * interdit `@testing-library/react` sur les composants — donc, hors de son
 * propre fichier, ce menu n'aurait aucune story et aucun test.
 *
 * @see docs/features/redaction-web-canvas/ — hub de la feature
 */
import * as React from 'react';
import { prepareCanvasDocx } from '../state/prepareCanvasDocx';
import {
  canvasDurableBoundaryIsCurrent,
  captureCanvasDurableBoundary,
  checkpointMatchesCanvasDurableBoundary,
} from '../state/canvasDurableBoundary';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { canvasDraftApi, isCanvasDocxSuperseded } from '../api/canvasDraft.api';
import { annoncerVersionDepassee } from './annoncerVersionDepassee';
import { CANVAS_EXPORT_BLOCKED_HINT, isCanvasSessionProtectedByWork } from '../state/canvasSessionProtection';
import { useCanvasDocumentScope } from '../state/canvasDocumentScopeContext';
import { Button } from '@/components/ui/button';
import { triggerBlobDownload } from '@/utils/blobDownload';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Le libellé ne nomme plus le format : le bouton ouvre un menu qui en propose
 * deux (2026-08-26). Le nommer ici ferait mentir le nom accessible pour l'une
 * des deux sorties.
 */
const TELECHARGER_LABEL = 'Télécharger le document';

/** Formats de sortie proposés par le menu « Télécharger ». */
type FormatDeSortie = 'docx' | 'pdf';

const LIBELLE_DU_FORMAT: Record<FormatDeSortie, string> = {
  docx: 'Word (.docx)',
  pdf: 'PDF',
};


/**
 * Bouton « Télécharger » du header canvas — SEULE porte de sortie du document
 * jusqu'ici (audit métier 2026-07-30) : l'avocat pouvait rédiger un acte complet
 * sans aucun moyen de le récupérer pour le déposer ou l'envoyer.
 *
 * Depuis 2026-08-26 c'est un MENU à deux sorties, Word et PDF. Les deux partent
 * du même export SuperDoc (`triggerDownload:false` + ancre maison : le
 * téléchargement natif de SuperDoc nomme le fichier lui-même, sans le titre de
 * l'acte — trois actes téléchargés donneraient trois fichiers indistinguables).
 *
 * **Le PDF est rendu côté serveur, et ce n'est pas un détour.** SuperDoc ne sait
 * pas produire de PDF : son type `ExportType` annonce `'docx' | 'pdf' | 'html'`,
 * mais `export()` n'appelle que `exportEditorsToDOCX` et ne pousse un blob que
 * `if (exportType.includes("docx"))` — demander `['pdf']` rend une archive VIDE,
 * sans lever d'erreur (superdoc@2.6.0, vérifié 2026-08-26). Le type est donc un
 * piège : s'y fier livrerait un bouton qui télécharge un fichier illisible.
 */
export function CanvasDownloadMenu({ fileLabel, gele }: { fileLabel: string; gele: boolean }): React.JSX.Element {
  const scope = useCanvasDocumentScope();
  const [menuOuvert, setMenuOuvert] = React.useState(false);
  // Le format en cours, pas un booléen : la conversion PDF passe par LibreOffice
  // côté serveur et dure des secondes, là où l'export Word est immédiat. Savoir
  // LEQUEL travaille est ce qui rend l'attente lisible.
  const [formatEnCours, setFormatEnCours] = React.useState<FormatDeSortie | null>(null);
  // Doublon de l'état, lu au moment du clic : `telecharger` est mémoïsé sur
  // `fileLabel` et ne verrait pas un `formatEnCours` capturé au rendu précédent.
  const exportEnCoursRef = React.useRef(false);

  const telecharger = React.useCallback((format: FormatDeSortie) => {
    // `gele` est dans les dépendances (pas dans une ref comme l'export en cours) :
    // il change au rythme des tours d'agent, pas des frappes.
    // `gele` drives rendering, but an exact writer may have begun between the last
    // render and this click. Re-read this scope's exact session, never the
    // visible singleton, before exporting a live document.
    if (gele || isCanvasSessionProtectedByWork(scope.conversationId) || exportEnCoursRef.current) return;
    exportEnCoursRef.current = true;
    void (async () => {
      // The visible Canvas can belong to another conversation while this
      // resident remains hot. Export the instance owned by this portal's scope.
      const instance = scope.getPhysicalResources().instance;
      if (!instance) {
        toast.error('Canvas non initialisé. Ouvre ou génère un document avant de télécharger.');
        exportEnCoursRef.current = false;
        return;
      }
      setFormatEnCours(format);
      try {
        // Toujours .docx d'abord : c'est le seul format que SuperDoc produise.
        const controller = scope.getDraftController();
        const boundary = captureCanvasDurableBoundary({
          mountEpoch: scope.getSnapshot().mountEpoch,
          draft: controller.getSnapshot(),
        });
        if (!boundary) {
          toast.error('Le document doit être sauvegardé avant son téléchargement.');
          return;
        }
        const checkpoint = await controller.checkpoint();
        if (!checkpointMatchesCanvasDurableBoundary(boundary, {
          ...checkpoint,
          liveMountEpoch: scope.getSnapshot().mountEpoch,
          liveDraft: controller.getSnapshot(),
        })) {
          toast.error('Le document a changé pendant sa sauvegarde. Vérifie la version affichée puis recommence le téléchargement.');
          return;
        }
        const attemptId = checkpoint.attemptId;
        if (!attemptId) {
          toast.error('Le document doit être sauvegardé avant son téléchargement.');
          return;
        }
        // A browser can keep A mounted while another session publishes B. A
        // local export has no server-side copy operation, so validate A's ACK
        // without re-uploading a freshly generated (and byte-different) ZIP.
        await canvasDraftApi.verifyDraftAttempt(checkpoint.draftId, attemptId);
        if (!canvasDurableBoundaryIsCurrent(boundary, {
          liveMountEpoch: scope.getSnapshot().mountEpoch,
          liveDraft: scope.getDraftController().getSnapshot(),
        })) {
          toast.error('Le document a changé pendant sa vérification. Réessayez.');
          return;
        }
        const exported = await controller.exportDocx(instance);
        const prepared = await prepareCanvasDocx(new Uint8Array(await exported.arrayBuffer()), instance);
        if (prepared.status === 'rejected') {
          toast.error('Le document ne peut pas être téléchargé : une correction de format est nécessaire.');
          return;
        }
        if (!canvasDurableBoundaryIsCurrent(boundary, {
          liveMountEpoch: scope.getSnapshot().mountEpoch,
          liveDraft: scope.getDraftController().getSnapshot(),
        })) {
          toast.error('Le document a changé pendant le téléchargement. Réessayez.');
          return;
        }
        await canvasDraftApi.verifyDraftAttempt(checkpoint.draftId, attemptId);
        if (!canvasDurableBoundaryIsCurrent(boundary, {
          liveMountEpoch: scope.getSnapshot().mountEpoch,
          liveDraft: scope.getDraftController().getSnapshot(),
        })) {
          toast.error('Le document a changé pendant sa vérification. Réessayez.');
          return;
        }
        const docx = new Blob([new Uint8Array(prepared.bytes)], { type: exported.type });
        // Export is asynchronous. Do not turn bytes into a downloadable file
        // after the writer lease was acquired while SuperDoc was exporting.
        if (isCanvasSessionProtectedByWork(scope.conversationId)
          || !canvasDurableBoundaryIsCurrent(boundary, {
            liveMountEpoch: scope.getSnapshot().mountEpoch,
            liveDraft: scope.getDraftController().getSnapshot(),
          })) {
          toast.error(CANVAS_EXPORT_BLOCKED_HINT);
          return;
        }
        // Les octets de l'éditeur VIVANT partent en conversion, pas le brouillon
        // stocké : le PDF doit montrer ce que l'avocat a sous les yeux, sinon il
        // télécharge un acte en retard d'une frappe sans pouvoir s'en apercevoir.
        const blob = format === 'pdf' ? await canvasDraftApi.exportPdf(docx) : docx;
        // La conversion PDF est un second trajet asynchrone. Le document exact
        // peut avoir été pris par Hector pendant cet aller-retour : ne pas
        // livrer alors un instantané devenu périmé, même si l'export DOCX était
        // encore sûr quelques secondes plus tôt.
        if (isCanvasSessionProtectedByWork(scope.conversationId)
          || !canvasDurableBoundaryIsCurrent(boundary, {
            liveMountEpoch: scope.getSnapshot().mountEpoch,
            liveDraft: scope.getDraftController().getSnapshot(),
          })) {
          toast.error(CANVAS_EXPORT_BLOCKED_HINT);
          return;
        }
        await canvasDraftApi.verifyDraftAttempt(checkpoint.draftId, attemptId);
        if (!canvasDurableBoundaryIsCurrent(boundary, {
          liveMountEpoch: scope.getSnapshot().mountEpoch,
          liveDraft: scope.getDraftController().getSnapshot(),
        })) {
          toast.error('Le document a changé pendant sa vérification. Réessayez.');
          return;
        }
        // Nom lisible par l'avocat, assaini : / et \ casseraient le nom de fichier.
        triggerBlobDownload(blob, `${(fileLabel || 'document').replace(/[/\\]/g, '-')}.${format}`);
      } catch (err: unknown) {
        console.error(`[CanvasDownloadMenu] échec export ${format}`, err);
        if (isCanvasDocxSuperseded(err)) {
          annoncerVersionDepassee('Téléchargement impossible');
          return;
        }
        const detail = err instanceof Error ? err.message : null;
        toast.error(
          detail
            ? `Téléchargement impossible : ${detail}`
            : 'Téléchargement impossible. Réessaie dans quelques secondes.',
        );
      } finally {
        exportEnCoursRef.current = false;
        setFormatEnCours(null);
      }
    })();
  }, [fileLabel, gele, scope]);

  // Icône seule depuis #853 : l'état d'export vivait dans le libellé (« Export… »),
  // qui n'existe plus. Il passe donc dans l'icône (rotation + `aria-busy`) —
  // sans quoi un export lent ne se manifesterait par rien du tout.
  //
  // Le nom accessible, lui, NE BOUGE PAS pendant l'export : c'est l'identité du
  // bouton, pas son état. Le faire varier ferait disparaître le bouton de toute
  // recherche par son nom au moment précis où l'on veut l'observer.
  const enCours = formatEnCours ? ` · export ${LIBELLE_DU_FORMAT[formatEnCours]} en cours…` : '';
  const raison = gele ? CANVAS_EXPORT_BLOCKED_HINT : enCours;

  return (
    <DropdownMenu
      open={menuOuvert}
      // Le refus est ici plutôt que sur chaque entrée : un menu qui s'ouvre pour
      // ne proposer que des lignes mortes fait chercher à l'avocat ce qu'il a
      // raté. L'infobulle du déclencheur, elle, dit pourquoi (#893).
      onOpenChange={(ouvert) => { if (!(ouvert && gele)) setMenuOuvert(ouvert); }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              // PAS `disabled` : un bouton désactivé ne reçoit aucun événement de
              // pointeur, donc son infobulle Radix ne s'ouvre jamais — l'avocat
              // n'aurait qu'une icône qui tourne, sans pouvoir apprendre pourquoi.
              // `aria-disabled` + garde dans le gestionnaire donnent le même refus
              // en gardant l'élément survolable et focusable.
              className="h-8 w-8 shrink-0 aria-disabled:opacity-60 aria-disabled:cursor-default"
              aria-disabled={formatEnCours !== null || gele}
              aria-label={TELECHARGER_LABEL}
              aria-busy={formatEnCours !== null}
            >
              {formatEnCours !== null
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Download className="h-4 w-4" />}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">{`${TELECHARGER_LABEL}${raison}`}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-56">
        {(['docx', 'pdf'] as const).map((format) => (
          <DropdownMenuItem
            key={format}
            onSelect={() => telecharger(format)}
            className="gap-2"
          >
            <Download className="h-4 w-4 text-stone-500" />
            {LIBELLE_DU_FORMAT[format]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
